import { computed, DestroyRef, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CloudApiService } from './cloud-api.service';
import { TenantService } from './tenant.service';
import { routePattern } from '../models/portal-feedback.models';
import {
    AssistantBlock,
    AssistantConversationDetail,
    AssistantConversationSummary,
    AssistantDisplayItem,
    AssistantMention,
    AssistantMessage,
    AssistantPageContext,
    AssistantPendingAction,
    AssistantIssueProposal,
    ConfirmIssueProposal,
    AssistantPulse,
    AssistantScope,
    AssistantStatus,
    AssistantStreamEvent,
    parseSseFrames
} from '../models/assistant.models';

/** Where the user is, shown as a removable chip in the drawer header. */
export interface AssistantPageChip {
    kind: 'device' | 'location';
    id: string;
    label: string;
}

const HISTORY_KEY = 'assistant.recentPrompts';
const MAX_HISTORY = 30;
const DOCK_KEY = 'assistant.dock';
/** Below this the page would be too cramped beside the panel, so it always floats. */
const DOCK_MIN_WIDTH = '(min-width: 1440px)';

/** `docked` sits beside the page on wide screens; `overlay` slides over it. */
export type AssistantDockMode = 'docked' | 'overlay';

/**
 * State and API of the in-app assistant drawer: which scope is active (one tenant, or the service desk across
 * tenants), the pulse, conversations, and the streamed answer. Streaming uses `fetch` + `ReadableStream`
 * (HttpClient cannot hand out a body as it arrives), so it adds the bearer token itself and renews it once on 401.
 */
@Injectable({ providedIn: 'root' })
export class AssistantService {
    private api = inject(CloudApiService);
    private http = inject(HttpClient);
    private tenants = inject(TenantService);
    private router = inject(Router);
    private destroyRef = inject(DestroyRef);

    readonly open = signal(false);
    /** True while the full-page service desk is showing; the drawer stays closed and the page owns the conversation. */
    readonly fullPage = signal(false);
    /** Docked: on wide screens the panel sits beside the page instead of over it. Remembered per browser. */
    readonly dockPreference = signal<AssistantDockMode>(AssistantService.readDock());
    /** True when the window is wide enough to dock the panel and still leave the page usable. */
    readonly wideScreen = signal(AssistantService.matchWide()?.matches ?? false);
    /** The panel is open and docked, so the layout should make room for it. */
    readonly docked = computed(() => this.open() && !this.fullPage() && this.wideScreen() && this.dockPreference() === 'docked');
    /** Set once the first status check has finished, whether or not it succeeded. */
    readonly statusChecked = signal(false);
    readonly scope = signal<AssistantScope>('tenant');
    readonly status = signal<AssistantStatus | null>(null);
    readonly pulse = signal<AssistantPulse | null>(null);
    readonly pulseLoading = signal(false);
    readonly conversations = signal<AssistantConversationSummary[]>([]);
    readonly activeId = signal<string | null>(null);
    readonly activeTitle = signal<string | null>(null);
    readonly messages = signal<AssistantMessage[]>([]);
    /** Live state of every confirm card in view, keyed by pending action id. */
    readonly actions = signal<Record<string, AssistantPendingAction>>({});
    /** Live state of every drafted GitHub issue in view, keyed by proposal id. */
    readonly issueProposals = signal<Record<string, AssistantIssueProposal>>({});
    readonly streaming = signal(false);
    /** When the answer in progress was asked, for the status line's elapsed time. */
    readonly streamStartedAt = signal<number | null>(null);
    readonly loading = signal(false);
    readonly recentPrompts = signal<string[]>(AssistantService.readHistory());
    readonly page = signal<AssistantPageContext | null>(null);
    readonly pageChipDismissed = signal(false);

    private abort: AbortController | null = null;
    /** Bumped whenever the view moves to another conversation, tenant or scope; late responses for the old one are dropped. */
    private generation = 0;
    private statusFor: string | null | undefined = undefined;
    private loadedFor: string | null = null;

    readonly namespaceId = computed(() => (this.scope() === 'serviceDesk' ? null : this.tenants.selectedTenantId()));
    readonly tenantEnabled = computed(() => !!this.status()?.tenantEnabled);
    readonly serviceDeskAvailable = computed(() => !!this.status()?.serviceDesk?.available);
    /** The top-bar button shows when either scope can be used. */
    readonly available = computed(() => this.tenantEnabled() || this.serviceDeskAvailable());
    readonly canAct = computed(() => (this.scope() === 'serviceDesk' ? !!this.status()?.serviceDesk?.canProposeActions : !!this.status()?.canProposeActions));

    /** Suggestions from the latest answer, for chips and the composer's ghost text. */
    readonly suggestions = computed(() => {
        const list = this.messages();
        for (let i = list.length - 1; i >= 0; i--) {
            const m = list[i];
            if (m.role !== 'assistant') return [];
            const found = [...m.items].reverse().find((item) => item.kind === 'suggestions');
            if (found?.suggestions?.length) return found.suggestions;
        }
        return [];
    });

    /** Something wants the user's attention: a bad pulse figure, or a proposal awaiting confirmation. */
    readonly attention = computed(() => {
        const stats = this.pulse()?.stats ?? [];
        const waiting = (a: { status: string; expiresUtc: string }) => a.status === 'Proposed' && Date.parse(a.expiresUtc) > Date.now();
        const proposed = Object.values(this.actions()).some(waiting) || Object.values(this.issueProposals()).some(waiting);
        return proposed || stats.some((s) => s.value > 0 && (s.tone === 'danger' || s.tone === 'warn'));
    });

    readonly pageChip = computed<AssistantPageChip | null>(() => {
        const page = this.page();
        if (!page || this.pageChipDismissed()) return null;
        if (page.deviceId) {
            const device = (this.api.devices() ?? []).find((d) => d.id === page.deviceId);
            return { kind: 'device', id: page.deviceId, label: device?.displayName || device?.name || 'This device' };
        }
        if (page.locationId) {
            const location = (this.api.locations() ?? []).find((l: { id?: string }) => l?.id === page.locationId) as { name?: string } | undefined;
            return { kind: 'location', id: page.locationId, label: location?.name || 'This location' };
        }
        return null;
    });

    constructor() {
        const wide = AssistantService.matchWide();
        if (wide) {
            const onChange = (e: MediaQueryListEvent) => this.wideScreen.set(e.matches);
            wide.addEventListener('change', onChange);
            this.destroyRef.onDestroy(() => wide.removeEventListener('change', onChange));
        }
        this.page.set(this.capturePage());
        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                takeUntilDestroyed()
            )
            .subscribe(() => {
                this.page.set(this.capturePage());
                this.pageChipDismissed.set(false);
            });

        // Status and pulse follow the selected tenant.
        effect(() => {
            const tenant = this.tenants.selectedTenantId();
            const signedIn = !!this.api.currentUser();
            untracked(() => {
                if (!signedIn) return;
                this.loadStatus(tenant);
            });
        });

        // Opening refreshes the lists; switching scope or tenant also leaves the current conversation.
        effect(() => {
            const scope = this.scope();
            const ns = this.namespaceId();
            const open = this.open() || this.fullPage();
            untracked(() => {
                if (!open) return;
                const key = `${scope}|${ns ?? ''}`;
                if (key !== this.loadedFor) {
                    if (this.loadedFor !== null) this.startNew();
                    this.loadedFor = key;
                }
                this.loadConversations(scope, ns);
                this.loadPulse(scope, ns);
            });
        });
    }

    // ---- Drawer -----------------------------------------------------------------------------------

    toggle(): void {
        if (!this.available() || this.fullPage()) return;
        this.open.update((v) => !v);
    }

    /**
     * The full-page service desk is showing: close the drawer and work across every tenant. Leaving keeps the
     * service desk scope, so the drawer picks up the same conversation.
     */
    enterServiceDeskPage(): void {
        this.open.set(false);
        this.fullPage.set(true);
        if (this.serviceDeskAvailable() && this.scope() !== 'serviceDesk') this.setScope('serviceDesk');
    }

    leaveServiceDeskPage(): void {
        this.fullPage.set(false);
    }

    setScope(scope: AssistantScope): void {
        if (scope === 'serviceDesk' && !this.serviceDeskAvailable()) return;
        if (scope === 'tenant' && !this.tenantEnabled()) return;
        this.stop();
        this.scope.set(scope);
    }

    // ---- Status, pulse, conversations ------------------------------------------------------------

    async loadStatus(namespaceId: string | null, force = false): Promise<void> {
        if (!force && this.statusFor === namespaceId && this.status()) return;
        this.statusFor = namespaceId;
        try {
            const query = namespaceId ? `?namespaceId=${encodeURIComponent(namespaceId)}` : '';
            const status = await this.get<AssistantStatus>(`/assistant/status${query}`);
            if (this.statusFor !== namespaceId) return;
            this.status.set(status);
            // Keep the scope usable: fall back to whichever one this user can open.
            if (!status.tenantEnabled && status.serviceDesk?.available) this.scope.set('serviceDesk');
            else if (status.tenantEnabled && this.scope() === 'serviceDesk' && !status.serviceDesk?.available) this.scope.set('tenant');
            if (!status.tenantEnabled && !status.serviceDesk?.available) this.open.set(false);
        } catch {
            this.status.set(null);
            this.open.set(false);
        } finally {
            if (this.statusFor === namespaceId) this.statusChecked.set(true);
        }
    }

    async loadPulse(scope = this.scope(), namespaceId = this.namespaceId()): Promise<void> {
        if (scope === 'tenant' && !namespaceId) return;
        this.pulseLoading.set(true);
        try {
            this.pulse.set(await this.get<AssistantPulse>(`/assistant/pulse?${this.scopeQuery(scope, namespaceId)}`));
        } catch {
            this.pulse.set(null);
        } finally {
            this.pulseLoading.set(false);
        }
    }

    async loadConversations(scope = this.scope(), namespaceId = this.namespaceId()): Promise<void> {
        if (scope === 'tenant' && !namespaceId) return;
        try {
            this.conversations.set(await this.get<AssistantConversationSummary[]>(`/assistant/conversations?${this.scopeQuery(scope, namespaceId)}`));
        } catch {
            this.conversations.set([]);
        }
    }

    /** Back to the pulse view; the next message starts a new conversation. */
    startNew(): void {
        this.generation++;
        this.stop();
        this.activeId.set(null);
        this.activeTitle.set(null);
        this.messages.set([]);
        this.actions.set({});
        this.issueProposals.set({});
    }

    async openConversation(id: string): Promise<void> {
        const generation = ++this.generation;
        this.stop();
        this.loading.set(true);
        try {
            const detail = await this.get<AssistantConversationDetail>(`/assistant/conversations/${encodeURIComponent(id)}`);
            if (generation !== this.generation) return;
            this.activeId.set(detail.conversation.id);
            this.activeTitle.set(detail.conversation.title);
            this.messages.set(detail.messages ?? []);
            const actions: Record<string, AssistantPendingAction> = {};
            for (const m of detail.messages ?? []) for (const item of m.items) if (item.block?.type === 'action') actions[item.block.action.id] = item.block.action;
            this.actions.set({ ...actions, ...(detail.actions ?? {}) });
            const drafts: Record<string, AssistantIssueProposal> = {};
            for (const m of detail.messages ?? []) for (const item of m.items) if (item.block?.type === 'issueProposal') drafts[item.block.proposal.id] = item.block.proposal;
            this.issueProposals.set({ ...drafts, ...(detail.issueProposals ?? {}) });
        } finally {
            this.loading.set(false);
        }
    }

    async deleteConversation(id: string): Promise<void> {
        await firstValueFrom(this.http.delete<void>(this.url(`/assistant/conversations/${encodeURIComponent(id)}`), { headers: this.headers() }));
        this.conversations.update((list) => list.filter((c) => c.id !== id));
        if (this.activeId() === id) this.startNew();
    }

    // ---- Sending -----------------------------------------------------------------------------------

    async send(text: string, mentions: AssistantMention[] = []): Promise<void> {
        const message = text.trim();
        if (!message || this.streaming()) return;
        this.remember(message);

        const user: AssistantMessage = { id: `local-${Date.now()}`, role: 'user', createdUtc: new Date().toISOString(), items: [{ kind: 'text', text: message }] };
        const reply: AssistantMessage = { id: `reply-${Date.now()}`, role: 'assistant', createdUtc: new Date().toISOString(), items: [], streaming: true };
        this.messages.update((list) => [...list, user, reply]);
        this.streaming.set(true);
        this.streamStartedAt.set(Date.now());
        const abort = new AbortController();
        this.abort = abort;

        const generation = this.generation;
        const view = `${this.scope()}|${this.namespaceId() ?? ''}`;
        try {
            let id = this.activeId();
            if (!id) {
                const created = await firstValueFrom(this.http.post<AssistantConversationSummary>(this.url('/assistant/conversations'), { scope: this.scope(), namespaceId: this.namespaceId() }, { headers: this.headers() }));
                // The user moved to another tenant, scope or conversation meanwhile: don't bind this one to the new view.
                if (abort.signal.aborted || generation !== this.generation || view !== `${this.scope()}|${this.namespaceId() ?? ''}`) return;
                id = created.id;
                this.activeId.set(id);
                this.activeTitle.set(created.title);
                this.conversations.update((list) => [created, ...list.filter((c) => c.id !== created.id)]);
            }

            const page = this.pageForSend(mentions);
            const response = await this.streamRequest(`/assistant/conversations/${encodeURIComponent(id)}/messages`, { text: message, page }, abort.signal);
            if (!response.ok || !response.body) {
                const body = await response.text().catch(() => '');
                this.applyEvent({ type: 'error', data: { code: String(response.status), message: AssistantService.errorText(response.status, body) } });
                return;
            }
            const finished = await this.readStream(response.body, abort.signal);
            // The server always ends with done or error; a stream that just stops means the answer was lost.
            if (!finished && !abort.signal.aborted && generation === this.generation) this.applyEvent({ type: 'error', data: { code: 'incomplete', message: 'The answer stopped unexpectedly. Try asking again.' } });
        } catch (err) {
            if (abort.signal.aborted) this.applyEvent({ type: 'error', data: { code: 'stopped', message: 'Stopped.' } });
            else this.applyEvent({ type: 'error', data: { code: 'network', message: 'Could not reach the assistant. Check your connection and try again.' } });
        } finally {
            if (this.abort === abort) this.abort = null;
            this.streaming.set(false);
            this.streamStartedAt.set(null);
            this.messages.update((list) => list.map((m) => (m.streaming ? { ...m, streaming: false } : m)));
        }
    }

    stop(): void {
        this.abort?.abort();
        this.abort = null;
    }

    /** Applies one stream event to the reply being written (the last message). */
    applyEvent(event: AssistantStreamEvent): void {
        if (event.type === 'conversation') {
            this.activeTitle.set(event.data.title);
            this.conversations.update((list) => list.map((c) => (c.id === event.data.id ? { ...c, title: event.data.title } : c)));
            return;
        }
        if (event.type === 'done') return;
        if (event.type === 'block' && event.data.block?.type === 'action') this.trackAction(event.data.block.action);
        if (event.type === 'block' && event.data.block?.type === 'issueProposal') this.trackIssueProposal(event.data.block.proposal);

        this.messages.update((list) => {
            const last = list[list.length - 1];
            if (!last || last.role !== 'assistant') return list;
            const items = [...last.items];
            const tail = items[items.length - 1];
            switch (event.type) {
                case 'text':
                    if (tail?.kind === 'text') items[items.length - 1] = { ...tail, text: (tail.text ?? '') + event.data.text };
                    else items.push({ kind: 'text', text: event.data.text });
                    break;
                case 'progress':
                    if (tail?.kind === 'progress') items[items.length - 1] = { ...tail, text: (tail.text ?? '') + event.data.text };
                    else items.push({ kind: 'progress', text: event.data.text });
                    break;
                case 'tool': {
                    const index = items.findIndex((i) => i.kind === 'tool' && i.toolId === event.data.id);
                    const item: AssistantDisplayItem = {
                        kind: 'tool',
                        toolId: event.data.id,
                        toolName: event.data.name,
                        toolStatus: event.data.status,
                        toolFailed: event.data.status === 'error'
                    };
                    if (index >= 0) items[index] = item;
                    else items.push(item);
                    break;
                }
                case 'block':
                    items.push({ kind: 'block', block: event.data.block as AssistantBlock });
                    break;
                case 'suggestions':
                    items.push({ kind: 'suggestions', suggestions: event.data.items });
                    break;
                case 'error':
                    items.push({ kind: 'error', text: event.data.message, code: event.data.code });
                    break;
            }
            return [...list.slice(0, -1), { ...last, items }];
        });
    }

    // ---- Actions -----------------------------------------------------------------------------------

    trackAction(action: AssistantPendingAction): void {
        this.actions.update((map) => ({ ...map, [action.id]: action }));
    }

    /** Quick-action button on a device card: a proposal without a model round trip, shown as a confirm card. */
    async proposeAction(deviceId: string, action: 'Reboot' | 'Restart', namespaceId: string | null): Promise<AssistantPendingAction> {
        const result = await firstValueFrom(
            this.http.post<{ action: AssistantPendingAction }>(
                this.url('/assistant/actions/propose'),
                { conversationId: this.activeId(), deviceId, action, scope: this.scope(), namespaceId: this.scope() === 'serviceDesk' ? namespaceId : this.namespaceId() },
                { headers: this.headers() }
            )
        );
        this.trackAction(result.action);
        if (this.messages().length === 0 || this.messages()[this.messages().length - 1].role !== 'assistant') {
            this.messages.update((list) => [...list, { id: `local-action-${result.action.id}`, role: 'assistant', createdUtc: new Date().toISOString(), items: [] }]);
        }
        this.applyEvent({ type: 'block', data: { block: { type: 'action', action: result.action } } });
        return result.action;
    }

    async decide(id: string, decision: 'confirm' | 'reject'): Promise<AssistantPendingAction> {
        try {
            const result = await firstValueFrom(this.http.post<{ action: AssistantPendingAction }>(this.url(`/assistant/actions/${encodeURIComponent(id)}/${decision}`), {}, { headers: this.headers() }));
            this.trackAction(result.action);
            return result.action;
        } catch (err) {
            // Refused (expired, permission gone): show the server's current view of it.
            await this.refreshAction(id).catch(() => undefined);
            throw err;
        }
    }

    async refreshAction(id: string): Promise<AssistantPendingAction> {
        const result = await this.get<{ action: AssistantPendingAction }>(`/assistant/actions/${encodeURIComponent(id)}`);
        this.trackAction(result.action);
        return result.action;
    }

    // ---- Issue proposals ---------------------------------------------------------------------------

    trackIssueProposal(proposal: AssistantIssueProposal): void {
        this.issueProposals.update((map) => ({ ...map, [proposal.id]: proposal }));
    }

    /** Files a drafted issue (with the person's edits) or declines it. */
    async decideIssue(id: string, decision: 'confirm' | 'reject', edits?: ConfirmIssueProposal): Promise<AssistantIssueProposal> {
        try {
            const result = await firstValueFrom(this.http.post<{ proposal: AssistantIssueProposal }>(this.url(`/assistant/issue-proposals/${encodeURIComponent(id)}/${decision}`), edits ?? {}, { headers: this.headers() }));
            this.trackIssueProposal(result.proposal);
            return result.proposal;
        } catch (err) {
            // Refused (a duplicate appeared, expired, permission gone): show the server's current view of it.
            await this.refreshIssueProposal(id).catch(() => undefined);
            throw err;
        }
    }

    async refreshIssueProposal(id: string): Promise<AssistantIssueProposal> {
        const result = await this.get<{ proposal: AssistantIssueProposal }>(`/assistant/issue-proposals/${encodeURIComponent(id)}`);
        this.trackIssueProposal(result.proposal);
        return result.proposal;
    }

    // ---- Internals ---------------------------------------------------------------------------------

    /** Applies every event of the stream; true when it ended properly (with done or error). */
    private async readStream(body: ReadableStream<Uint8Array>, abort: AbortSignal): Promise<boolean> {
        const reader = body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let finished = false;
        const apply = (e: { type: string; data: string }) => {
            try {
                this.applyEvent({ type: e.type, data: JSON.parse(e.data) } as AssistantStreamEvent);
                if (e.type === 'done' || e.type === 'error') finished = true;
            } catch {
                // A malformed frame is skipped rather than ending the answer.
            }
        };
        try {
            while (!abort.aborted) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const { events, rest } = parseSseFrames(buffer);
                buffer = rest;
                events.forEach(apply);
            }
            buffer += decoder.decode();
            parseSseFrames(buffer + '\n\n').events.forEach(apply);
            return finished;
        } finally {
            if (abort.aborted) await reader.cancel().catch(() => undefined);
        }
    }

    /** POSTs with the bearer token; on 401 renews the session once and retries. */
    private async streamRequest(path: string, body: unknown, signal: AbortSignal): Promise<Response> {
        const attempt = () =>
            fetch(this.url(path), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...(this.api.token() ? { Authorization: `Bearer ${this.api.token()}` } : {}) },
                body: JSON.stringify(body),
                credentials: 'include',
                signal
            });
        const first = await attempt();
        if (first.status !== 401) return first;
        if (!(await this.api.refreshSession())) return first;
        return attempt();
    }

    private pageForSend(mentions: AssistantMention[]): AssistantPageContext {
        const page = this.page() ?? this.capturePage();
        const chipGone = this.pageChipDismissed();
        return { ...page, deviceId: chipGone ? null : page.deviceId, locationId: chipGone ? null : page.locationId, mentions };
    }

    private capturePage(): AssistantPageContext {
        const root = this.router.routerState.snapshot.root;
        const route = routePattern(root);
        const params: Record<string, string> = {};
        let node: ActivatedRouteSnapshot | null = root;
        while (node) {
            Object.assign(params, node.params);
            node = node.firstChild;
        }
        const heading = typeof document !== 'undefined' ? document.querySelector('.layout-main h1, .layout-main h2, .layout-main h3')?.textContent?.replace(/\s+/g, ' ').trim() : null;
        const locationId = params['locationId'] ?? (route?.startsWith('/location') ? (params['id'] ?? null) : null);
        return {
            url: this.router.url,
            route,
            pageTitle: heading || null,
            deviceId: params['deviceId'] ?? null,
            locationId,
            mentions: []
        };
    }

    private remember(prompt: string): void {
        const next = [prompt, ...this.recentPrompts().filter((p) => p !== prompt)].slice(0, MAX_HISTORY);
        this.recentPrompts.set(next);
        try {
            sessionStorage.setItem(HISTORY_KEY, JSON.stringify(next));
        } catch {
            /* storage unavailable: history lasts for this page only */
        }
    }

    /** Switch between docked beside the page and floating over it; kept for next time. */
    setDock(mode: AssistantDockMode): void {
        this.dockPreference.set(mode);
        try {
            localStorage.setItem(DOCK_KEY, mode);
        } catch {
            /* storage unavailable: the choice lasts for this page only */
        }
    }

    private static readDock(): AssistantDockMode {
        try {
            return localStorage.getItem(DOCK_KEY) === 'overlay' ? 'overlay' : 'docked';
        } catch {
            return 'docked';
        }
    }

    private static matchWide(): MediaQueryList | null {
        return typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(DOCK_MIN_WIDTH) : null;
    }

    private static readHistory(): string[] {
        try {
            const raw = sessionStorage.getItem(HISTORY_KEY);
            const parsed: unknown = raw ? JSON.parse(raw) : [];
            return Array.isArray(parsed) ? parsed.filter((p): p is string => typeof p === 'string').slice(0, MAX_HISTORY) : [];
        } catch {
            return [];
        }
    }

    private static errorText(status: number, body: string): string {
        try {
            const parsed = JSON.parse(body) as { error?: string; message?: string };
            if (parsed?.error || parsed?.message) return parsed.error ?? parsed.message ?? '';
        } catch {
            /* not JSON */
        }
        if (status === 404) return 'This conversation is no longer available.';
        if (status === 429) return 'You’ve reached the assistant’s message limit for now. Try again later.';
        return 'The assistant could not answer. Try again.';
    }

    private scopeQuery(scope: AssistantScope, namespaceId: string | null): string {
        return scope === 'serviceDesk' ? 'scope=serviceDesk' : `scope=tenant&namespaceId=${encodeURIComponent(namespaceId ?? '')}`;
    }

    private get<T>(path: string): Promise<T> {
        return firstValueFrom(this.http.get<T>(this.url(path), { headers: this.headers() }));
    }

    private url(path: string): string {
        return `${environment.apiBaseUrl.replace(/\/$/, '')}${path}`;
    }

    private headers(): HttpHeaders {
        const token = this.api.token();
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        if (token) headers = headers.set('Authorization', `Bearer ${token}`);
        return headers;
    }
}
